///
/// EarthBrowser
///
/// Created by Matt Giger on September 19, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// Ops dashboard: summary strip (open tasks, disputes, subscriptions, new
/// users), the service health board (heartbeats from crons/ingests), and the
/// admin task inbox with resolve/snooze/dismiss actions. Tasks are produced
/// by system events (chargebacks, failing/stale heartbeats); see
/// documentation/planning/ADMIN_OPS_DASHBOARD_PLAN.md.
///

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/components/ui/use-toast'
import {
  useDismissOpsTaskMutation,
  useGetOpsHealthQuery,
  useGetOpsSummaryQuery,
  useGetOpsTasksQuery,
  useLazyLookupShareQuery,
  useResolveOpsTaskMutation,
  useSnoozeOpsTaskMutation,
  useTakedownShareMutation,
  type AdminShare,
  type OpsTask,
  type OpsTaskSeverity,
  type ServiceHealth,
} from '@/redux/api/opsApi'
import {
  Activity,
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock,
  CreditCard,
  Inbox,
  ShieldOff,
  TrendingDown,
  TrendingUp,
  UserPlus,
  X,
} from 'lucide-react'
import { useState } from 'react'

const PAGE_SIZE = 50

const severityStyles: Record<OpsTaskSeverity, string> = {
  urgent: 'bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/70',
  warn: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/70',
  info: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/70',
}

function formatAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(ms / 60000)
  if (minutes < 60) return `${Math.max(minutes, 0)}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function StatTile({ icon, label, value, sub, accent }: {
  icon: React.ReactNode
  label: string
  value: string
  sub?: React.ReactNode
  accent: string
}) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
      <div className={`p-2 rounded-lg ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold leading-tight">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  )
}

function SummaryStrip() {
  const { data: summary, isLoading } = useGetOpsSummaryQuery()

  if (isLoading || !summary) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
      </div>
    )
  }

  const openTotal = summary.openTasks.urgent + summary.openTasks.warn + summary.openTasks.info
  const usersDelta = summary.newUsers.thisWeek - summary.newUsers.lastWeek
  const attentionAccent = summary.openTasks.urgent > 0
    ? 'bg-red-500/10 text-red-600 dark:text-red-400'
    : openTotal > 0
      ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
      : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatTile
        icon={openTotal > 0 ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
        label="Open Tasks"
        value={String(openTotal)}
        sub={openTotal > 0
          ? `${summary.openTasks.urgent} urgent · ${summary.openTasks.warn} warn · ${summary.openTasks.info} info`
          : 'All clear'}
        accent={attentionAccent}
      />
      <StatTile
        icon={<CreditCard className="h-4 w-4" />}
        label="Open Disputes"
        value={String(summary.openDisputes)}
        sub={summary.openDisputes > 0 ? 'Respond in Stripe' : 'None pending'}
        accent={summary.openDisputes > 0
          ? 'bg-red-500/10 text-red-600 dark:text-red-400'
          : 'bg-slate-500/10 text-slate-600 dark:text-slate-400'}
      />
      <StatTile
        icon={<CheckCircle2 className="h-4 w-4" />}
        label="Active Subscriptions"
        value={summary.activeSubscriptions.toLocaleString()}
        accent="bg-teal-500/10 text-teal-600 dark:text-teal-400"
      />
      <StatTile
        icon={<UserPlus className="h-4 w-4" />}
        label="New Users (7d)"
        value={summary.newUsers.thisWeek.toLocaleString()}
        sub={
          <span className="inline-flex items-center gap-1">
            {usersDelta >= 0
              ? <TrendingUp className="h-3 w-3 text-emerald-500" />
              : <TrendingDown className="h-3 w-3 text-red-500" />}
            {usersDelta >= 0 ? '+' : ''}{usersDelta} vs. prior week
          </span>
        }
        accent="bg-blue-500/10 text-blue-600 dark:text-blue-400"
      />
    </div>
  )
}

const healthDot: Record<ServiceHealth['status'], string> = {
  ok: 'bg-emerald-500',
  stale: 'bg-amber-500',
  failing: 'bg-red-500',
}

function lastRunLabel(service: ServiceHealth): string {
  if (service.status === 'failing' && service.last_error) {
    return `failed ${formatAge(service.last_error)}`
  }
  return service.last_success ? `ok ${formatAge(service.last_success)}` : 'never succeeded'
}

// Service health board: red/amber units get full cards with the error detail;
// healthy units collapse into a compact chip row so a fully green board stays
// one glance tall. Data comes from service_heartbeat via /api/admin/ops/health.
function HealthBoard() {
  const { data, isLoading } = useGetOpsHealthQuery(undefined, { pollingInterval: 60_000 })

  if (isLoading) {
    return <Skeleton className="h-24 rounded-xl" />
  }

  const services = data?.services ?? []
  if (services.length === 0) return null

  const unhealthy = services.filter(service => service.status !== 'ok')
  const healthy = services.filter(service => service.status === 'ok')

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Service Health</h3>
        <span className="text-xs text-muted-foreground">
          {unhealthy.length === 0
            ? `all ${services.length} healthy`
            : `${unhealthy.filter(service => service.status === 'failing').length} failing · ${unhealthy.filter(service => service.status === 'stale').length} stale · ${healthy.length} ok`}
        </span>
      </div>

      {unhealthy.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {unhealthy.map(service => (
            <div key={service.id} className="flex items-start gap-2.5 p-3 rounded-lg border border-border bg-background">
              <span className={`h-2.5 w-2.5 rounded-full mt-1 shrink-0 ${healthDot[service.status]}`} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{service.label}</span>
                  <Badge variant="outline" className="rounded-full font-mono text-[10px]">{service.kind}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  <span className="capitalize">{service.status}</span> — {lastRunLabel(service)}
                  {service.last_success && service.status === 'failing' && ` · last success ${formatAge(service.last_success)}`}
                </p>
                {service.status === 'failing' && service.last_error_message && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1 break-words">{service.last_error_message}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {healthy.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {healthy.map(service => (
            <span
              key={service.id}
              title={`${service.id} · ${lastRunLabel(service)}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-border bg-background text-xs text-muted-foreground"
            >
              <span className={`h-1.5 w-1.5 rounded-full ${healthDot.ok}`} />
              {service.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const cSlugPattern = /^[0-9A-Za-z]{10}$/

/** Accepts a bare 10-char slug or a full share URL (…/?s=<slug>) */
function extractShareSlug(input: string): string | null {
  const trimmed = input.trim()
  if (cSlugPattern.test(trimmed)) return trimmed
  try {
    const slug = new URL(trimmed).searchParams.get('s')
    if (slug && cSlugPattern.test(slug)) return slug
  } catch {
    // not a URL
  }
  return null
}

// Share kill switch: look up a reported share link, review what it is and who
// owns it, then take it down (audited with a note). Moderation stopgap until
// the full report pipeline ships with photo trips / story tours.
function ShareTakedown() {
  const [input, setInput] = useState('')
  const [note, setNote] = useState('')
  const [share, setShare] = useState<AdminShare | null>(null)
  const [lookupShare, { isFetching }] = useLazyLookupShareQuery()
  const [takedownShare, { isLoading: removing }] = useTakedownShareMutation()

  const handleLookup = async () => {
    const slug = extractShareSlug(input)
    if (!slug) {
      toast({ title: 'Invalid share link', description: 'Paste a share URL or its 10-character id.', variant: 'destructive' })
      return
    }
    try {
      const result = await lookupShare(slug).unwrap()
      setShare(result.share)
    } catch {
      setShare(null)
      toast({ title: 'Share not found', description: 'It may already have been deleted.', variant: 'destructive' })
    }
  }

  const handleTakedown = async () => {
    if (!share) return
    try {
      await takedownShare({ slug: share.slug, note: note.trim() || undefined }).unwrap()
      toast({ title: 'Share taken down', description: `${share.slug} no longer resolves.` })
      setShare(null)
      setInput('')
      setNote('')
    } catch {
      toast({ title: 'Takedown failed', description: 'Please try again.', variant: 'destructive' })
    }
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <ShieldOff className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Share Takedown</h3>
        <span className="text-xs text-muted-foreground">audited kill switch for reported share links</span>
      </div>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={e => { setInput(e.target.value); setShare(null) }}
          onKeyDown={e => { if (e.key === 'Enter') void handleLookup() }}
          placeholder="Paste a share URL or its slug (…/?s=aB3xY9kQ2m)"
          aria-label="Share URL or slug"
          className="rounded-xl"
        />
        <Button
          variant="outline"
          onClick={() => void handleLookup()}
          disabled={isFetching || input.trim().length === 0}
          className="rounded-full shrink-0 transition-all duration-300 focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          {isFetching ? 'Looking up…' : 'Look up'}
        </Button>
      </div>
      {share && (
        <div className="p-3 rounded-lg border border-border bg-background space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{share.title}</span>
            <Badge variant="outline" className="rounded-full font-mono text-[10px]">{share.share_type}</Badge>
            <span className="text-xs text-muted-foreground">
              created {formatAge(share.created_at)} · {share.view_count} view{share.view_count === 1 ? '' : 's'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Owner: {share.owner_email}{share.owner_username ? ` (${share.owner_username})` : ''} · user #{share.user_id}
          </p>
          <div className="flex gap-2">
            <Input
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Reason (recorded in the audit log)"
              aria-label="Takedown reason"
              className="rounded-xl"
            />
            <Button
              variant="destructive"
              onClick={() => void handleTakedown()}
              disabled={removing}
              className="rounded-full shrink-0 transition-all duration-300 focus-visible:ring-2 focus-visible:ring-red-500"
            >
              {removing ? 'Removing…' : 'Take down'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function TaskActions({ task }: { task: OpsTask }) {
  const [resolveTask, { isLoading: resolving }] = useResolveOpsTaskMutation()
  const [snoozeTask, { isLoading: snoozing }] = useSnoozeOpsTaskMutation()
  const [dismissTask, { isLoading: dismissing }] = useDismissOpsTaskMutation()
  const [snoozeOpen, setSnoozeOpen] = useState(false)
  const busy = resolving || snoozing || dismissing

  const act = async (fn: () => Promise<unknown>, message: string) => {
    try {
      await fn()
      toast({ title: message })
    } catch {
      toast({ title: 'Action failed', description: 'Please try again.', variant: 'destructive' })
    }
  }

  return (
    <div className="flex items-center gap-1 shrink-0">
      <Button
        variant="ghost"
        size="icon"
        disabled={busy}
        aria-label="Resolve task"
        title="Resolve"
        className="hover:bg-emerald-500/10"
        onClick={() => act(() => resolveTask({ id: task.id }).unwrap(), 'Task resolved')}
      >
        <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
      </Button>
      <Popover open={snoozeOpen} onOpenChange={setSnoozeOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            disabled={busy}
            aria-label="Snooze task"
            title="Snooze"
            className="hover:bg-amber-500/10"
          >
            <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-40 p-1 rounded-xl" align="end">
          {[{ days: 1, label: '1 day' }, { days: 7, label: '1 week' }, { days: 30, label: '30 days' }].map(opt => (
            <button
              key={opt.days}
              className="w-full text-left text-sm px-3 py-2 rounded-lg hover:bg-accent transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              onClick={() => {
                setSnoozeOpen(false)
                act(() => snoozeTask({ id: task.id, days: opt.days }).unwrap(), `Snoozed for ${opt.label}`)
              }}
            >
              Snooze {opt.label}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      <Button
        variant="ghost"
        size="icon"
        disabled={busy}
        aria-label="Dismiss task"
        title="Dismiss"
        className="hover:bg-red-500/10"
        onClick={() => act(() => dismissTask({ id: task.id }).unwrap(), 'Task dismissed')}
      >
        <X className="h-4 w-4 text-muted-foreground" />
      </Button>
    </div>
  )
}

function TaskRow({ task, showActions }: { task: OpsTask, showActions: boolean }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card">
      <Badge className={`rounded-full capitalize shrink-0 mt-0.5 ${severityStyles[task.severity]}`}>
        {task.severity}
      </Badge>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold">{task.title}</span>
          <Badge variant="outline" className="rounded-full font-mono text-[10px]">{task.type}</Badge>
          <span className="text-xs text-muted-foreground">{formatAge(task.created_at)}</span>
          {task.status === 'snoozed' && task.snoozed_until && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              snoozed until {new Date(task.snoozed_until).toLocaleDateString()}
            </span>
          )}
        </div>
        {task.body && <p className="text-sm text-muted-foreground mt-1">{task.body}</p>}
        {task.entity_ref?.user_email && (
          <p className="text-xs text-muted-foreground mt-1">
            <a
              href={task.entity_ref.user_id
                ? `?section=users&user=${task.entity_ref.user_id}`
                : `?section=users`}
              className="underline underline-offset-2 hover:text-foreground transition-all duration-300"
            >
              {task.entity_ref.user_email}
            </a>
          </p>
        )}
        {task.resolved_at && (
          <p className="text-xs text-muted-foreground mt-1">
            {task.status === 'done' ? 'Resolved' : 'Dismissed'}{' '}
            {task.resolved_by_email ? `by ${task.resolved_by_email}` : 'automatically'}{' '}
            {formatAge(task.resolved_at)}
            {task.resolution_note && ` — ${task.resolution_note}`}
          </p>
        )}
      </div>
      {showActions && <TaskActions task={task} />}
    </div>
  )
}

export function OpsSection() {
  const [statusFilter, setStatusFilter] = useState('open')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useGetOpsTasksQuery({
    status: statusFilter,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    page,
  })

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/70 bg-red-500/10 p-6 text-center">
        <p className="font-semibold text-red-600 dark:text-red-400">Error loading Ops dashboard</p>
        <p className="text-sm mt-2 text-muted-foreground">Please check your permissions and try again.</p>
      </div>
    )
  }

  const tasks = data?.tasks ?? []
  const total = data?.pagination.total ?? 0
  const pageCount = Math.max(1, data?.pagination.pages ?? 1)
  const showActions = statusFilter === 'open' || statusFilter === 'snoozed'

  return (
    <div className="space-y-5">
      <SummaryStrip />
      <HealthBoard />

      <div className="flex items-center gap-3 flex-wrap">
        <Tabs
          value={statusFilter}
          onValueChange={(value) => { setStatusFilter(value); setPage(1) }}
        >
          <TabsList className="rounded-full">
            <TabsTrigger value="open" className="rounded-full">Open</TabsTrigger>
            <TabsTrigger value="snoozed" className="rounded-full">Snoozed</TabsTrigger>
            <TabsTrigger value="done" className="rounded-full">Resolved</TabsTrigger>
            <TabsTrigger value="dismissed" className="rounded-full">Dismissed</TabsTrigger>
          </TabsList>
        </Tabs>
        {(data?.types.length ?? 0) > 0 && (
          <Select
            value={typeFilter}
            onValueChange={(value: string) => { setTypeFilter(value); setPage(1) }}
          >
            <SelectTrigger className="w-[180px] rounded-xl focus:ring-2 focus:ring-blue-500" aria-label="Filter by task type">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="all" className="rounded-lg">All Types</SelectItem>
              {data?.types.map(type => (
                <SelectItem key={type} value={type} className="rounded-lg font-mono text-xs">{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full rounded-xl" />
          <Skeleton className="h-20 w-full rounded-xl" />
          <Skeleton className="h-20 w-full rounded-xl" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-8 flex flex-col items-center justify-center text-center">
          <div className="p-3 rounded-full bg-emerald-500/10 mb-4">
            {statusFilter === 'open'
              ? <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              : <Inbox className="h-8 w-8 text-emerald-500" />}
          </div>
          <h3 className="text-base font-semibold mb-1">
            {statusFilter === 'open' ? 'All clear' : 'Nothing here'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {statusFilter === 'open'
              ? 'No tasks need your attention right now.'
              : `No ${statusFilter === 'done' ? 'resolved' : statusFilter} tasks${typeFilter !== 'all' ? ' of this type' : ''}.`}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {tasks.map(task => (
              <TaskRow key={task.id} task={task} showActions={showActions} />
            ))}
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, total)} of {total} tasks
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
                >
                  Previous
                </Button>
                <span className="text-sm px-2">Page {page} of {pageCount}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pageCount}
                  className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <ShareTakedown />
    </div>
  )
}
